/**
 * VideoLibrary.js — every cartoon MP4 the Motion Studio has ever
 * rendered, browseable and downloadable in one place.
 *
 * Data comes from GET /api/motion-studio/library with three filters:
 *   • grade_tier (dropdown)     — K-5 / 6-8 / 9-12 / Seminary / Adult
 *   • search (text)             — matches lesson_id + prompt
 *   • pagination (skip + limit)
 *
 * Each card renders an inline <video controls> plus a native
 * `<a download>` Download button so students can save the file to
 * disk or share it offline.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;
const TIERS = ["", "K-5", "6-8", "9-12", "Seminary", "Adult"];

function _fmtSeconds(s) {
  if (!Number.isFinite(s)) return "";
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function _slugify(str) {
  return String(str || "clip")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "clip";
}

export default function VideoLibrary() {
  const [tier, setTier] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [videos, setVideos] = useState([]);
  const [byTrack, setByTrack] = useState({});
  const [ready, setReady] = useState(0);
  const [regenerating, setRegenerating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Debounce the search box so we don't fire a request every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setSkip(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const params = { limit: PAGE_SIZE, skip };
      if (tier) params.grade_tier = tier;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await axios.get(`${API}/motion-studio/library`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 15000,
      });
      setVideos(data.videos || []);
      setTotal(data.total || 0);
      setByTrack(data.by_track || {});
      setReady(data.ready || 0);
      setRegenerating(data.regenerating || 0);
    } catch (e) {
      setError(e?.friendlyMessage || e?.message || "Couldn't load the library");
    } finally {
      setLoading(false);
    }
  }, [tier, debouncedSearch, skip]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const trackChips = useMemo(() => {
    const label = { k5: "K-5", se: "Special Edition", nal: "Name & Lineage" };
    return Object.entries(byTrack).map(([k, v]) => ({
      key: k, label: label[k] || k, count: v,
    }));
  }, [byTrack]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const canPrev = skip > 0;
  const canNext = skip + PAGE_SIZE < total;

  return (
    <div className="card" data-testid="video-library" style={{ padding: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ color: "#FFD700", margin: "0 0 4px 0" }}>🎬 Video Library</h2>
          <p style={{ color: "#aaa", margin: 0, fontSize: "0.85rem" }}>
            Every cartoon lesson the Motion Studio has rendered.
            Play in place or download for offline study.
          </p>
        </div>
        <div style={{ color: "#888", fontSize: "0.8rem", textAlign: "right" }}>
          <div><strong style={{ color: "#FFD700" }}>{total}</strong> total clips</div>
          {regenerating > 0 && (
            <div
              data-testid="video-library-regen-progress"
              style={{ marginTop: 4, color: "#FFA500" }}
              title="Every lesson has a 8–10 minute Eli video now. Lessons still waiting for their own video play the shared General Edition until it is ready."
            >
              <strong>{ready}</strong> lesson editions ready
              <span style={{ color: "#888" }}> · {regenerating} playing the General Edition</span>
            </div>
          )}
          <div style={{ marginTop: 4 }}>
            {trackChips.map(c => (
              <span key={c.key} style={{ marginLeft: 8 }}>
                {c.label}: <strong style={{ color: "#eee" }}>{c.count}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <input
          data-testid="video-library-search"
          type="search"
          placeholder="Search by lesson or topic…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: 200,
            background: "#111", color: "#eee",
            border: "1px solid #333", borderRadius: 6,
            padding: "8px 12px", fontSize: "0.9rem",
          }}
        />
        <select
          data-testid="video-library-tier"
          value={tier}
          onChange={(e) => { setTier(e.target.value); setSkip(0); }}
          style={{
            background: "#111", color: "#eee",
            border: "1px solid #333", borderRadius: 6,
            padding: "8px 12px", fontSize: "0.9rem",
            width: "auto",
          }}
        >
          {TIERS.map(t => (
            <option key={t || "all"} value={t}>{t || "All grades"}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="alert alert-error" data-testid="video-library-error" style={{ marginTop: 14 }}>
          {error}
          <button onClick={fetchPage} style={{ marginLeft: 10, background: "transparent", color: "#FFD700", border: "1px solid #FFD70088", padding: "4px 10px", borderRadius: 6, cursor: "pointer" }}>Retry</button>
        </div>
      )}

      {loading && (
        <div data-testid="video-library-loading" style={{ color: "#aaa", padding: "30px 0", textAlign: "center" }}>
          <div className="spinner" style={{ margin: "0 auto 10px" }}></div>
          Loading videos…
        </div>
      )}

      {!loading && !error && videos.length === 0 && (
        <div style={{ color: "#888", padding: "30px 0", textAlign: "center" }}>
          No videos match those filters yet.
        </div>
      )}

      {!loading && videos.length > 0 && (
        <div style={{
          marginTop: 18,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 16,
        }}>
          {videos.map(v => {
            const isUpgrading = v.status === "preparing" || !v.video_url;
            const isHeroFallback = v.status === "general_edition";
            const badgeText = isHeroFallback
              ? "GENERAL EDITION"
              : isUpgrading
                ? "PREPARING ELI VIDEO"
                : "";
            const borderColor = isHeroFallback
              ? "#FFD70088"
              : isUpgrading
                ? "#8B7355"
                : "#FFD70055";
            return (
              <div key={v.cache_key} data-testid={`video-card-${v.cache_key}`} style={{
                background: "#111",
                borderRadius: 10,
                border: `1px solid ${borderColor}`,
                overflow: "hidden",
                position: "relative",
              }}>
                {isUpgrading ? (
                  <div
                    data-testid={`video-preparing-${v.cache_key}`}
                    style={{
                      height: 180, display: "flex", alignItems: "flex-end", justifyContent: "center",
                      background: "url(/eli/poster.jpg) center / cover", color: "#FFD700",
                      fontSize: "0.75rem", paddingBottom: 10,
                    }}
                  >
                    <span style={{ background: "#000000cc", padding: "4px 10px", borderRadius: 6 }}>
                      Eli is preparing this video
                    </span>
                  </div>
                ) : (
                  <video
                    data-testid={`video-player-${v.cache_key}`}
                    src={v.video_url}
                    poster="/eli/poster.jpg"
                    controls preload="none" playsInline
                    style={{ width: "100%", height: 180, background: "#000", display: "block" }}
                  >Your browser can't play this video.</video>
                )}
                {badgeText && (
                  <div
                    data-testid={`video-status-badge-${v.cache_key}`}
                    style={{
                      position: "absolute", top: 8, left: 8,
                      background: "rgba(0,0,0,0.78)",
                      color: isHeroFallback ? "#FFD700" : "#FFA500",
                      padding: "3px 8px",
                      fontSize: "0.65rem", fontWeight: 700,
                      letterSpacing: 0.4, borderRadius: 4,
                      border: `1px solid ${isHeroFallback ? "#FFD70088" : "#FFA50055"}`,
                    }}
                    title={isHeroFallback
                      ? "This plays the shared Eli General Edition. The lesson's own Eli video is being made and will replace it automatically."
                      : "A 8–10 minute Eli video of this lesson is being prepared and will appear here automatically."}
                  >
                    {badgeText}
                  </div>
                )}
                <div style={{ padding: "12px 14px 14px" }}>
                  <div style={{
                    color: "#FFD700", fontSize: "0.9rem", fontWeight: 600,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }} title={v.lesson_title || v.prompt}>
                    {v.lesson_title || v.prompt || v.lesson_id}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.72rem", marginTop: 3 }}>
                    {v.grade_tier || "—"}{v.duration_seconds > 0 ? ` · ${_fmtSeconds(v.duration_seconds)}` : ""}
                    {isHeroFallback && (
                      <span style={{ color: "#FFD700", marginLeft: 6 }}>· General Edition · lesson version coming</span>
                    )}
                    {isUpgrading && !isHeroFallback && (
                      <span style={{ color: "#FFA500", marginLeft: 6 }}>· coming soon</span>
                    )}
                    {!isUpgrading && !isHeroFallback && (
                      <span style={{ color: "#90EE90", marginLeft: 6 }}>· ✓ authentic</span>
                    )}
                  </div>
                  {!isUpgrading && <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <a
                      data-testid={`download-video-${v.cache_key}`}
                      href={v.download_url || v.video_url}
                      download={`ylc-${_slugify(v.lesson_title || v.prompt || v.lesson_id)}.mp4`}
                      style={{
                        padding: "6px 12px", borderRadius: 6, textDecoration: "none",
                        background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                        color: "#111", fontWeight: 700, fontSize: "0.75rem",
                        border: "1px solid #FFD70088", flex: "0 0 auto",
                      }}
                    >⬇︎ Download</a>
                    <button
                      data-testid={`copy-link-${v.cache_key}`}
                      onClick={() => {
                        const url = `${process.env.REACT_APP_BACKEND_URL}${v.video_url}`;
                        try { navigator.clipboard.writeText(url); } catch { /* ignore */ }
                      }}
                      style={{
                        padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                        background: "transparent", color: "#87CEEB",
                        border: "1px solid #87CEEB88", fontSize: "0.75rem",
                        flex: "0 0 auto", width: "auto",
                      }}
                    >🔗 Copy link</button>
                  </div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > PAGE_SIZE && (
        <div style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          gap: 12, marginTop: 20, color: "#aaa", fontSize: "0.85rem",
        }}>
          <button
            data-testid="video-library-prev"
            disabled={!canPrev}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            style={{
              padding: "6px 14px", borderRadius: 6, cursor: canPrev ? "pointer" : "not-allowed",
              background: "transparent", color: canPrev ? "#FFD700" : "#555",
              border: `1px solid ${canPrev ? "#FFD70088" : "#33333388"}`,
              fontSize: "0.85rem", flex: "0 0 auto", width: "auto",
            }}
          >← Prev</button>
          <span>Page {currentPage} of {pageCount}</span>
          <button
            data-testid="video-library-next"
            disabled={!canNext}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            style={{
              padding: "6px 14px", borderRadius: 6, cursor: canNext ? "pointer" : "not-allowed",
              background: "transparent", color: canNext ? "#FFD700" : "#555",
              border: `1px solid ${canNext ? "#FFD70088" : "#33333388"}`,
              fontSize: "0.85rem", flex: "0 0 auto", width: "auto",
            }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}
